import { useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { mergeScanRows, SCAN_STATUS, ScanResultRow, toScanResultRow } from '@/services/return/returnReceiptReceive/types'
import { useGetReceiptScanHistory, useReceiveReturnReceipt } from '@/services/return/returnReceiptReceive/useReturnReceiptReceiveQueries'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import CenterReceiptWorkPageView, { type FormState, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { CENTER_OPTIONS, MESSAGES, RECEIPT_SCAN_HISTORY_FLAG } from './constants'
import { useScanFeedback } from './hooks/useScanFeedback'
import { fromSearchQuery, Rgr08FilterFormValues, Rgr08SearchQuery, toHistoryFilter, toSearchQuery } from './searchQuery'

/**
 * RGR08 센터도착 수령작업 — 화면(겉)은 컨덕터 생성본(`handoff/page.generated.tsx`)이고 이 파일은 배선이다.
 *
 * 조회 화면처럼 보이지만 스캔 1건이 곧 쓰기 트랜잭션이다(수령 판정). 판정은 서버가 한다
 * (moms-be `ReturnReceiptReceiveService`) — 여기서는 결과만 받아 갈라 놓는다.
 *
 * 스캔 바의 값(등록센터·지정일·반송번호)은 **생성본이 들고 있다**(폼 버스 `rgr08ScanBar`).
 * 배선은 ⓐ `form` 슬롯으로 조회조건 복원값을 얹고 ⓑ 스캔 슬롯이 받는 `ctx.defaults`(생성본이 그 칸
 * 값을 실어 준다)로 요청을 만든다. **스캔이 끝나면 생성본이 반송번호 칸을 비우고 그 칸으로 포커스를
 * 돌린다**(성공·거부 모두) — 값 버스를 가진 쪽이 해야 하는 일이라 배선은 그 칸을 건드리지 않는다.
 */
/** 등록센터 선택지 — 렌더마다 새 배열을 만들지 않는다(옵션 슬롯은 매 렌더 불린다). */
const CENTER_SELECT_OPTIONS = CENTER_OPTIONS.map((option) => ({ value: option.value, label: option.label }))

const CENTER_FIELD = 'rgr08Center'
const DATE_FIELD = 'rgr08Date'

/** 폼 버스 값 → 옛 폼 값 모양. 지정일은 단일 달력이라 from=to 로 눕힌다(옛 화면도 `[0]` 만 썼다). */
const asText = (value: unknown): string => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '')

const toFormValues = (bar: FormState): Partial<Rgr08FilterFormValues> => {
  const center = asText(bar[CENTER_FIELD])
  const raw = bar[DATE_FIELD]
  const date = typeof raw === 'string' && raw ? dayjs(raw) : null
  if (!center || !date?.isValid()) return { centerNm: center }
  return { centerNm: center, assignDateRange: [date, date] }
}

function Rgr08V2() {
  // 개별 함수로 받는다 — `useScanFeedback` 이 돌려주는 **객체**는 매 렌더 새 참조라 그대로 deps 에 넣으면
  //  wiring useMemo 가 매번 다시 만들어진다. 두 함수는 `useCallback(fn, [])` 이라 참조가 고정이다.
  const { playSuccess, playError } = useScanFeedback()
  const menuSession = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<Rgr08SearchQuery>()
  const { mutateAsync } = useReceiveReturnReceipt()

  // 마운트 시 한 번만 계산한다(URL 이 바뀌어도 다시 계산하지 않는다 — 사용자가 고른 값이 방금 저장한
  // URL 값으로 되돌아가는 순환이 된다). 복원의 출처는 세션이 아니라 URL 이다(공통 결정서 D16).
  const [restored] = useState(() => fromSearchQuery(parsedQs))

  const [scanRows, setScanRows] = useState<ScanResultRow[]>([])
  const [historyFilter, setHistoryFilter] = useState(() => toHistoryFilter(restored))

  const { data: isHistoryQueryEnabled = false } = useFeatureFlag(RECEIPT_SCAN_HISTORY_FLAG)
  const { rows: historyRows } = useGetReceiptScanHistory(historyFilter, isHistoryQueryEnabled)

  // 폼 버스의 지금 값 — 슬롯이 렌더마다 채운다. 스캔 요청이 지정일을 읽을 때 쓴다.
  const barRef = useRef<FormState>({})

  /**
   * 조회조건(등록센터·지정일)을 **배선으로 끌어올린다**.
   *
   * 폼 버스는 생성본(자식) 소유다 — 그 칸을 바꾸면 **자식만 리렌더되고 이 엔트리는 리렌더되지 않는다**.
   * 그래서 ref 에 적어 두고 효과에서 읽는 방식은 아예 돌지 않는다(효과가 실행될 렌더 자체가 없다).
   * 슬롯이 값 변화를 감지해 **렌더 뒤(마이크로태스크)** 로 끌어올리고, 그 상태가 아래 효과의 의존값이 된다.
   * 렌더 중에 곧장 setState 하면 "다른 컴포넌트를 렌더 중에 갱신" 경고가 난다 — 그래서 한 틱 미룬다.
   */
  const [barFilter, setBarFilter] = useState<{ centerNm: string; assignDate: string }>(() => ({
    centerNm: restored.centerNm,
    assignDate: restored.assignDateRange?.[0]?.format('YYYY-MM-DD') ?? '',
  }))
  const barFilterRef = useRef(barFilter)
  const syncedFilterRef = useRef<string | null>(null)

  /**
   * 조회조건 유지 — 메뉴 세션·URL 에 남겨 다른 메뉴를 다녀와도 유지한다(D16). 반송번호는 대상이 아니다(D3).
   * 바뀐 조건으로 수령 이력을 다시 조회하고, 앞 조건의 스캔 행은 비운다.
   */
  useEffect(() => {
    const values = toFormValues({ [CENTER_FIELD]: barFilter.centerNm, [DATE_FIELD]: barFilter.assignDate })
    if (!values.centerNm || !values.assignDateRange) return
    const query = toSearchQuery(values as Rgr08FilterFormValues)
    const key = `${query.centerNm ?? ''}|${query.assignDateFrom ?? ''}|${query.assignDateTo ?? ''}`
    if (key === syncedFilterRef.current) return
    const first = syncedFilterRef.current === null
    syncedFilterRef.current = key
    // 마운트 복원값은 이미 URL·세션에 있던 값이다 — 진입마다 기본값을 다시 찍고 메뉴를 reload 하지 않는다
    // (옛 `Rgr08Page` 는 `appliedRangeRef` 를 복원값으로 심어 실제 변경 때만 썼다).
    if (first) return
    MenuStorage.menuOnSession.setSearch(query)
    menuSession.reload()
    replaceQuery(query)
    setHistoryFilter(toHistoryFilter(values))
    // 조건이 바뀌면 앞 조건에서 스캔한 행은 남기지 않는다 — 표가 지금 조건의 결과만 담게 한다.
    //  **플래그 OFF 경로에서는 일부러 비우지 않는다(1.0 동작 일치)** — 이력이 오지 않아 비우면 표가 통째로
    //  비고 다시 채울 것이 없다. 그 경로에서는 조건을 바꿔도 이번 세션 스캔 행이 표에 남는다(옛 화면과 같다).
    if (isHistoryQueryEnabled) setScanRows([])
    // `restored` 는 `useState` 초기값이라 마운트 뒤 참조가 고정된다 — deps 에 넣지 않아도 된다.
  }, [barFilter, isHistoryQueryEnabled, menuSession, replaceQuery])

  const displayRows = useMemo(() => mergeScanRows(scanRows, historyRows), [scanRows, historyRows])

  const permission = usePermissionSlot('rgr14')
  const wiring = useMemo<Wiring>(
    () => ({
      permission,
      // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
      columnLayout: { rgr08ResultTable: coreTableLayoutSlot('RGR14_HEAD') },
      rows: {
        // 데모 목데이터는 쓰지 않는다 — 이번 세션 스캔분 + 조회조건에 걸린 수령 이력(LOGISPRD-12610).
        //  이중 캐스팅인 이유: `ScanResultRow` 는 인터페이스라 인덱스 시그니처가 없어 `Record<string, unknown>`
        //  으로 바로 못 간다(`as Row[]` 하나면 TS2352). 표 컬럼의 dataIndex 와 필드 이름이 1:1 이라 값은 맞는다.
        ROWS: () => displayRows as unknown as Row[],
      },
      actions: {
        /**
         * 스캔 1회 — 호출·판정 분기·행 누적(계획서 5장 4단계).
         *
         * `ctx.defaults` 에 스캔 바의 지금 값이 실려 온다(생성본 `rowFromFields`).
         * 비성공 판정은 `{ error: 문구 }` 로 돌려 생성본이 안내창을 띄우게 한다 — 성공 토스트는 건너뛴다.
         * 중복 요청은 생성본이 버튼을 잠가 막는다(요청이 도는 동안 `disabled`).
         */
        rgr08ScanButton: async (ctx) => {
          const rtnInvoiceNo = asText(ctx.defaults.RTN_INVOICE_NO).trim()
          const centerNm = asText(ctx.defaults.IN_CENTER)
          // 빈 입력은 요청하지 않는다. false = 성공인데 알릴 문구가 없음(토스트 생략) — 실패가 아니다.
          if (!rtnInvoiceNo) return false
          const assignDate = toFormValues(barRef.current).assignDateRange?.[0]?.format('YYYY-MM-DD')

          try {
            const res = await mutateAsync({ centerNm, rtnInvoiceNo, assignDate, usrId: AuthStorage.user.get().email || '' })
            if (!res.rtnChk) {
              /*
               * 처리 거부(`rtnChk=false`)는 **방금 쏜 그 한 건**이 처리되지 않았다는 뜻이다(오입고·미등록 같은
               * 판정이 아니다 — 그건 `true` 로 온다). 그러므로 **앞서 성공한 행은 건드리지 않는다.**
               *
               * 1.0·옛 `useScanWorkflow` 는 여기서 표를 비웠다(`setRows([])`). 그때는 표가 스캔 응답만 쌓는
               * 메모장이라 '초기화' 가 말이 됐지만, 2.0 은 서버 기록(이력)까지 함께 그리는 표다. 비우면
               * **서버에 저장된 행이 화면에서만 사라지고** 작업자는 그걸 알 수 없다 — 다시 스캔해 중복스캔이
               * 쌓인다(moms-fe #824 리뷰 재현). 게다가 지우고 다시 불러오는 방식도 확실하지 않다: 이력은
               * 마감일자로 조회하는데 지정일과 어긋나면 그 행이 안 걸린다(searchQuery.toHistoryFilter 주석).
               *
               * 스캔은 반송번호 1건당 1요청이고 각각 따로 저장된다 — C 가 실패해도 A·B 는 서버에 그대로다.
               * 그래서 남겨 두는 편이 서버 상태와 일치한다. PD 확정(2026-09-29).
               *
               * 경고음은 울린다 — 작업자는 화면을 안 보고 소리로 판단한다(LOGISPRD-14197, 1.0 은 무음).
               */
              playError()
              return { error: res.message || MESSAGES.scanFailedFallback }
            }
            // rtnChk=true 면 rtnData 는 항상 1건이다(moms-be `ReceiveReturnReceiptResponseBody.of`).
            const row = res.rtnData[0]
            if (!row) return false
            setScanRows((prev) => [toScanResultRow(row), ...prev])
            if (row.SCAN_STATUS === SCAN_STATUS.SUCCESS) {
              playSuccess()
              return // 생성본이 '정상입고' 토스트를 낸다
            }
            playError()
            // 오입고면 '{실출고센터} 오입고', 그 밖은 판정명(미등록·중복스캔) — 옛 화면 문구 그대로.
            return { error: row.SCAN_STATUS === SCAN_STATUS.MISS ? [row.GI_WH_NM, '오입고'].filter(Boolean).join(' ') : row.SCAN_STATUS }
          } catch {
            // 네트워크·5xx — 같은 안내창으로 알린다(창을 닫으면 생성본이 스캔 칸으로 돌아간다).
            //  경고음도 울린다(LOGISPRD-14197) — 처리 안 된 스캔이 무음이면 작업자가 성공으로 착각한다.
            playError()
            return { error: MESSAGES.scanFailedFallback }
          }
        },
      },
      options: {
        // 화면 상수(1.0 도 하드코딩 5종). TODO(spec): 운영 센터 목록 확인 — 서버 조회 전환은 이번 범위 밖.
        RGR08CENTER_OPTIONS: () => CENTER_SELECT_OPTIONS,
      },
      form: {
        rgr08ScanBar: (state) => {
          // 복원값은 **아직 사용자가 고르지 않은 칸에만** 얹는다(한 번 고른 값이 되돌아가지 않게).
          //  반송번호는 건드리지 않는다 — 스캔이 끝나면 **생성본이** 그 칸을 비우고 포커스를 돌린다.
          //  여기(파생값)에서 비우려 하면 같은 코드 재스캔이 조용히 묵살된다(#824 리뷰 — 실제로 그렇게 깨졌다).
          //  비우기 계약은 `seam.test.tsx` 가 잠근다.
          const effective: FormState = {
            ...state,
            [CENTER_FIELD]: state[CENTER_FIELD] ?? restored.centerNm,
            [DATE_FIELD]: state[DATE_FIELD] ?? restored.assignDateRange?.[0]?.format('YYYY-MM-DD'),
          }
          barRef.current = effective
          // 조회조건이 바뀌면 배선으로 끌어올린다(렌더 중 setState 금지 — 한 틱 미룬다).
          const next = { centerNm: asText(effective[CENTER_FIELD]), assignDate: asText(effective[DATE_FIELD]) }
          if (next.centerNm !== barFilterRef.current.centerNm || next.assignDate !== barFilterRef.current.assignDate) {
            barFilterRef.current = next
            queueMicrotask(() => setBarFilter(next))
          }
          return effective
        },
      },
    }),
    [displayRows, mutateAsync, playSuccess, playError, restored, permission],
  )

  return <CenterReceiptWorkPageView wiring={wiring} />
}

export default Rgr08V2
